/**
 * Eircode validation for the nurse portal -- a MIRROR of the kiosk's
 * `quote-helper/src/shared/eircode.ts`, with the opposite contract.
 *
 * ⛔ THIS COPY GATES. The kiosk's docblock says "ADVISORY ONLY, by hard
 * requirement... the server never validates against it", and that is TRUE OF
 * THE KIOSK and false here. Board 2190 (Alex, 2026-09-18) made the nurse's
 * "Somewhere else" destination an Eircode that `validateRequestInput` REFUSES
 * when it is missing or malformed, so a driver always has an exact location.
 * Do not carry the kiosk's wording across; the two contracts differ on purpose.
 *
 * WHY A COPY AND NOT AN IMPORT. There is no root `package.json`, `portal`
 * depends on nothing from `quote-helper`, and `portal/tsconfig.json` has no
 * `paths`. The repo's convention is duplicate plus a cross-app parity spec; the
 * live precedent is `quote-helper/test/core/job-ref-parity.spec.ts`, which may
 * import across the tree only because its target imports node builtins alone
 * (the kiosk CI job runs one `npm ci`, so `portal/node_modules` is absent and a
 * cross-tree import of anything else fails TS2307). This module imports
 * NOTHING, so it qualifies. `quote-helper/test/shared/eircode.spec.ts` grades
 * the two copies for BEHAVIOUR, never for file text.
 *
 * ⚠️ There is a THIRD copy of this pattern, and it is NOT kept in step:
 * `portal/src/core/pii-redact.ts` masks Eircodes with a case-sensitive variant
 * that excludes `D6W`. Recorded on Q45, not fixed here.
 */

/**
 * ⚠️ `D6W` needs its own alternative: `D` is inside `[AC-FHKNPRTV-Y]` but `6W`
 * is not `[0-9]{2}`, and D6W is the one Irish routing key that is not
 * letter-digit-digit (pinned real at `quote-helper/src/shared/gazetteer.ts`
 * :178,:184). The unit charset excludes B, G, I, J, L, M, O, Q, S, U and Z, so
 * a value like `D01 AB12` is NOT a valid Eircode however plausible it looks.
 */
export const EIRCODE_RE = /^(?:D6W|[AC-FHKNPRTV-Y][0-9]{2})\s?[0-9AC-FHKNPRTV-Y]{4}$/i;

export function looksLikeEircode(value: string): boolean {
  return EIRCODE_RE.test(value.trim());
}

/**
 * Canonical STORE form: uppercase, one space after the 3-char routing key.
 *
 * ⛔ IT NEVER REJECTS, and it is not a guard: `normaliseEircode('n/a')` is
 * `'N/A'`. Always pair it with `looksLikeEircode`, never substitute it.
 * It returns `null` only for null/undefined/blank, so callers must handle that
 * before passing the result to `looksLikeEircode`, whose parameter is
 * non-nullable and would throw on `null`.
 */
export function normaliseEircode(value: string | null | undefined): string | null {
  if (value == null) return null;
  const compact = value.replace(/\s+/g, '').toUpperCase();
  if (compact === '') return null;
  if (compact.length <= 3) return compact;
  return `${compact.slice(0, 3)} ${compact.slice(3)}`;
}

/** A destination box split into its required Eircode and its optional hint. */
export interface ParsedDestination {
  /** Always canonical: uppercase, one space after the routing key. */
  readonly eircode: string;
  /** Free text for the driver. Empty string when the nurse typed none. */
  readonly hint: string;
}

/**
 * Leading and trailing commas and blanks, which the split leaves behind.
 *
 * ⚠️ `/g` MAKES THIS STATEFUL, so it is safe with `String.replace` (which resets
 * `lastIndex`) and NOT with `.test()` or `.exec()`, where a module-level `/g`
 * regex silently skips every other match. It is deliberately not exported. If
 * you ever need to TEST with it, build a local copy rather than reusing this one.
 */
const HINT_EDGE_RE = /^[\s,]+|[\s,]+$/g;

/**
 * Pull a required Eircode and an optional place hint out of ONE box.
 *
 * ⭐ IT ACCEPTS BOTH ORDERS, DELIBERATELY. Rungs 1-4 look for a LEADING
 * Eircode (`A98 X123, Bray seafront`), rungs 5-7 for a TRAILING one
 * (`4 Church Rd, Bray, A98 X2Y3`). The trailing rungs exist because three
 * independent authors in this repo wrote place-then-Eircode fixtures and the
 * QA script instructs that order, so refusing it would have refused the way
 * people actually write an Irish address. Leading rungs run FIRST, so a value
 * carrying two Eircode-shaped tokens resolves to the leading one.
 *
 * ⚠️ MATCHING IS WIDE ON PURPOSE. `normaliseEircode` strips ALL internal
 * whitespace before the test, so `A9 8XY12` and even `A 9 8 X 1 2 3` are
 * ACCEPTED as `A98 XY12` / `A98 X123`. That is wanted: this repo's own
 * canonicaliser already defines the store form, and refusing what it happily
 * canonicalises would refuse a pasted `A98  X123` on the one field board 2190
 * makes mandatory. A refusal fixture for routing-key SHAPE must therefore
 * survive normalisation -- use `98A X123`, never `A9 8XY12`.
 *
 * ⚠️ Shape is not existence. A structurally valid token is claimed as the
 * Eircode (`the car park at stand C15 PARK`), because `C15 PARK` IS a valid
 * shape. The words survive in the hint and Helen's kiosk confirm stays the
 * human gate.
 *
 * ⛔ THE PRODUCT OF THOSE TWO RULES, MEASURED AND PINNED (security review L1,
 * board 2190). Leading-wins and shape-is-not-existence COMPOSE into a case
 * neither one states on its own: an eircode-SHAPED place fragment in the first
 * two words outranks a real Eircode typed at the end.
 *   `C15 PARK entrance, A98 X2Y3` -> `C15 PARK, entrance, A98 X2Y3`
 * The nurse's genuine code is demoted into the hint. It is narrow -- the shaped
 * pair must be the FIRST two words, so `Ward C15 PARK, Bray, A98 X2Y3`
 * correctly resolves to `A98 X2Y3` -- and nothing is LOST, because the whole
 * string survives in the stored value either way. Accepted rather than fixed:
 * preferring a trailing match would break the far commoner leading form, and
 * no rung order gets both without knowing which token is a real Eircode, which
 * is the existence question this module cannot answer. `eircode.spec.ts` pins
 * both halves so the trade-off cannot drift unnoticed.
 *
 * @returns the parse, or `null` when no rung yields a valid Eircode.
 */
export function parseDestinationEircode(raw: string): ParsedDestination | null {
  const trimmed = raw.trim();
  if (trimmed === '') return null;

  const words = trimmed.split(/\s+/);
  const firstComma = trimmed.indexOf(',');
  const lastComma = trimmed.lastIndexOf(',');

  // [candidate, the remainder that becomes the hint]. `null` skips the rung.
  const rungs: ReadonlyArray<readonly [string | null, string]> = [
    [trimmed, ''],
    firstComma >= 0
      ? [trimmed.slice(0, firstComma), trimmed.slice(firstComma + 1)]
      : [null, ''],
    [words.slice(0, 2).join(' '), words.slice(2).join(' ')],
    [words[0] ?? null, words.slice(1).join(' ')],
    lastComma >= 0
      ? [trimmed.slice(lastComma + 1), trimmed.slice(0, lastComma)]
      : [null, ''],
    [words.slice(-2).join(' '), words.slice(0, -2).join(' ')],
    [words[words.length - 1] ?? null, words.slice(0, -1).join(' ')],
  ];

  for (const [candidate, remainder] of rungs) {
    if (candidate === null) continue;
    const normalised = normaliseEircode(candidate);
    if (normalised !== null && looksLikeEircode(normalised)) {
      return { eircode: normalised, hint: remainder.replace(HINT_EDGE_RE, '') };
    }
  }
  return null;
}

/**
 * The value stored in `requests.destination_text`: canonical, Eircode FIRST,
 * whatever order the nurse typed.
 *
 * ⚠️ IDEMPOTENT, and the spec pins it: re-parsing a formatted value returns it
 * unchanged, because a trailing-rung result is re-claimed by LEADING rung 2 on
 * the second pass, so the ladder converges after one step.
 *
 * ⚠️ It can GROW the input by at most 2 characters -- one space inserted into
 * an unspaced Eircode, plus a canonical separator replacing a one-character
 * one. Both `validateRequestInput` and the form's input bound account for that;
 * see `DESTINATION_TEXT_MAX_LEN` in `request-schema.ts`.
 */
export function formatDestination(parsed: ParsedDestination): string {
  return parsed.hint === '' ? parsed.eircode : `${parsed.eircode}, ${parsed.hint}`;
}
